/**
 * Página de paquetes regulatorios del Backoffice.
 * Lista paquetes paginados y permite descargar solo los finalizados.
 *
 * @taskId T657
 * @taskId T659
 * @see functional/Cycles/cycle-5/tasks.md HU-99-FE
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flexibilitypay/ui';

import type { BadgeProps } from '@flexibilitypay/ui';
import {
  type RegulatoryPackage,
  type RegulatoryPackageStatus,
} from '../api/regulatory-packages.api';
import {
  useRegulatoryPackageDownload,
  useRegulatoryPackages,
} from '../hooks/use-regulatory-packages';

const PAGE_SIZE = 10;
const ALL_STATUSES = 'ALL';
const STATUS_FILTERS: Array<RegulatoryPackageStatus | typeof ALL_STATUSES> = [
  ALL_STATUSES,
  'COMPLETED',
  'GENERATING',
  'FAILED',
  'PENDING',
  'WARNING',
  'BLOCKED',
];

function displayValue(value: string | undefined): string {
  return value && value.length > 0 ? value : '-';
}

function displayDate(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function getStatusVariant(status: RegulatoryPackageStatus): BadgeProps['variant'] {
  if (status === 'COMPLETED') return 'success';
  if (status === 'GENERATING') return 'warning';
  if (status === 'FAILED') return 'destructive';
  return 'outline';
}

interface StatusBadgeProps {
  status: RegulatoryPackageStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation(['regulatoryPackages']);
  return (
    <Badge variant={getStatusVariant(status)}>
      {t(`regulatoryPackages:status.${status}`)}
    </Badge>
  );
}

interface DownloadButtonProps {
  pkg: RegulatoryPackage;
  isPending: boolean;
  onDownload: (pkg: RegulatoryPackage) => void;
}

function DownloadButton({ pkg, isPending, onDownload }: DownloadButtonProps) {
  const { t } = useTranslation(['regulatoryPackages']);
  const canDownload = pkg.status === 'COMPLETED';
  const title = canDownload
    ? t('regulatoryPackages:actions.download')
    : t('regulatoryPackages:actions.downloadDisabled', {
        status: t(`regulatoryPackages:status.${pkg.status}`),
      });

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!canDownload || isPending}
      title={title}
      aria-label={title}
      onClick={() => onDownload(pkg)}
    >
      <Download className="h-4 w-4" />
      <span className="sr-only">{t('regulatoryPackages:actions.download')}</span>
    </Button>
  );
}

function LoadingRows() {
  return Array.from({ length: 4 }, (_, index) => (
    <TableRow key={index}>
      <TableCell colSpan={7}>
        <Skeleton className="h-8 w-full" />
      </TableCell>
    </TableRow>
  ));
}

export function RegulatoryPackagesPage() {
  const { t } = useTranslation(['regulatoryPackages']);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RegulatoryPackageStatus | typeof ALL_STATUSES>(ALL_STATUSES);

  const status = statusFilter === ALL_STATUSES ? undefined : statusFilter;
  const packagesQuery = useRegulatoryPackages({ page, size: PAGE_SIZE, status });
  const downloadMutation = useRegulatoryPackageDownload();

  const data = packagesQuery.data;
  const packages = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const hasPreviousPage = page > 0;
  const hasNextPage = totalPages > 0 && page + 1 < totalPages;

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as RegulatoryPackageStatus | typeof ALL_STATUSES);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">
          {t('regulatoryPackages:page.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('regulatoryPackages:page.subtitle')}
        </p>
      </div>

      <Card>
        <CardHeader className="gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>{t('regulatoryPackages:table.title')}</CardTitle>
            <CardDescription>{t('regulatoryPackages:table.description')}</CardDescription>
          </div>
          <div className="w-full space-y-1.5 md:w-56">
            <Label htmlFor="regulatory-package-status">
              {t('regulatoryPackages:filters.status.label')}
            </Label>
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger
                id="regulatory-package-status"
                aria-label={t('regulatoryPackages:filters.status.label')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((filter) => (
                  <SelectItem key={filter} value={filter}>
                    {filter === ALL_STATUSES
                      ? t('regulatoryPackages:filters.status.all')
                      : t(`regulatoryPackages:status.${filter}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {packagesQuery.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('regulatoryPackages:errors.title')}</AlertTitle>
              <AlertDescription>{t('regulatoryPackages:errors.list')}</AlertDescription>
            </Alert>
          )}

          {downloadMutation.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('regulatoryPackages:errors.title')}</AlertTitle>
              <AlertDescription>{t('regulatoryPackages:errors.download')}</AlertDescription>
            </Alert>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('regulatoryPackages:table.columns.period')}</TableHead>
                <TableHead>{t('regulatoryPackages:table.columns.type')}</TableHead>
                <TableHead>{t('regulatoryPackages:table.columns.tenant')}</TableHead>
                <TableHead>{t('regulatoryPackages:table.columns.status')}</TableHead>
                <TableHead>{t('regulatoryPackages:table.columns.hash')}</TableHead>
                <TableHead>{t('regulatoryPackages:table.columns.date')}</TableHead>
                <TableHead className="text-right">{t('regulatoryPackages:table.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packagesQuery.isLoading && <LoadingRows />}

              {!packagesQuery.isLoading && packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    {t('regulatoryPackages:empty')}
                  </TableCell>
                </TableRow>
              )}

              {!packagesQuery.isLoading && packages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell className="font-medium">
                    {displayValue(pkg.periodYyyymm ?? pkg.period)}
                  </TableCell>
                  <TableCell>{displayValue(pkg.reportType)}</TableCell>
                  <TableCell>{displayValue(pkg.tenantId)}</TableCell>
                  <TableCell>
                    <StatusBadge status={pkg.status} />
                  </TableCell>
                  <TableCell className="max-w-48 truncate font-mono text-xs text-muted-foreground">
                    {displayValue(pkg.fileHashSha256)}
                  </TableCell>
                  <TableCell>{displayDate(pkg.completedAt ?? pkg.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <DownloadButton
                      pkg={pkg}
                      isPending={downloadMutation.isPending}
                      onDownload={(selectedPackage) => downloadMutation.mutate(selectedPackage)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {t('regulatoryPackages:pagination.summary', {
                current: totalPages === 0 ? 0 : page + 1,
                total: totalPages,
              })}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage || packagesQuery.isFetching}
                onClick={() => setPage((current) => Math.max(0, current - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('regulatoryPackages:pagination.previous')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!hasNextPage || packagesQuery.isFetching}
                onClick={() => setPage((current) => current + 1)}
              >
                {t('regulatoryPackages:pagination.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}